import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Seo from "@/components/Seo";
import { formatAZN, CountdownBadge, STATUS_LABELS, STATUS_STYLE, useCountdown } from "@/lib/format";
import { Heart, Gauge, MapPin, Fuel, Calendar, Palette, Cog, CheckCircle2, XCircle, Phone, Mail, User } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1592309905620-e5b59f6dcb98?w=1600&q=80";

export default function AuctionDetailPage() {
  const { slug } = useParams();
  const { user, authHeaders } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [gallery, setGallery] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/auctions/${slug}`);
    setData(data);
  };
  useEffect(() => { load(); }, [slug]);
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); }, [slug]);

  const seoSchema = useMemo(() => {
    if (!data) return null;
    const a = data.auction;
    const images = a.images?.length ? a.images : [FALLBACK];
    return {
      "@context": "https://schema.org",
      "@type": "Vehicle",
      "name": `${a.vehicle.brand} ${a.vehicle.model} ${a.vehicle.year}`,
      "brand": { "@type": "Brand", "name": a.vehicle.brand },
      "model": a.vehicle.model,
      "vehicleModelDate": String(a.vehicle.year),
      "bodyType": a.vehicle.body_type,
      "fuelType": a.vehicle.fuel_type,
      "vehicleTransmission": a.vehicle.transmission,
      "color": a.vehicle.color,
      "mileageFromOdometer": { "@type": "QuantitativeValue", "value": a.vehicle.mileage, "unitCode": "KMT" },
      "image": images.slice(0, 5),
      "description": a.vehicle.description || `${a.vehicle.brand} ${a.vehicle.model} ${a.vehicle.year}`,
      "offers": {
        "@type": "Offer",
        "price": a.current_price,
        "priceCurrency": "AZN",
        "availability": a.status === "active" ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
        "validThrough": a.end_time,
      },
    };
  }, [data]);

  if (!data) return <div className="max-w-7xl mx-auto p-10 animate-pulse"><div className="h-96 bg-slate-100 rounded-2xl" /></div>;

  const a = data.auction;
  const bids = data.bids;
  const minNext = a.bid_count > 0 ? a.current_price + a.min_bid_increment : a.starting_price;
  const isOwner = user && user.id === a.seller_id;
  const isEnded = a.status === "ended_pending_seller";
  const isDecided = a.status === "accepted" || a.status === "rejected";
  const images = a.images?.length ? a.images : [FALLBACK];

  const placeBid = async () => {
    if (!user) { nav("/login"); return; }
    const amount = parseFloat(bidAmount);
    if (!amount || amount < minNext) { toast.error(`Minimum təklif: ${formatAZN(minNext)}`); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auctions/${a.id}/bids`, { amount }, { headers: authHeaders() });
      toast.success("Təklif qəbul edildi");
      setBidAmount("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Xəta baş verdi");
    } finally { setSubmitting(false); }
  };

  const decide = async (decision) => {
    try {
      await axios.post(`${API}/auctions/${a.id}/decide`, { decision }, { headers: authHeaders() });
      toast.success(decision === "accept" ? "Satış təsdiqləndi" : "Təklif rədd edildi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Xəta"); }
  };

  const toggleFav = async () => {
    if (!user) { nav("/login"); return; }
    await axios.post(`${API}/auctions/${a.id}/favorite`, {}, { headers: authHeaders() });
    toast.success("Favorilər yeniləndi");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 fade-in pb-32 lg:pb-10">
      <Seo
        title={`${a.vehicle.brand} ${a.vehicle.model} ${a.vehicle.year}`}
        description={`${a.vehicle.brand} ${a.vehicle.model} ${a.vehicle.year}, ${a.vehicle.body_type}, ${a.vehicle.mileage?.toLocaleString()} km, ${a.vehicle.fuel_type}, ${a.vehicle.city}. Cari qiymət: ${a.current_price?.toLocaleString()} AZN. Təklif sayı: ${a.bid_count}.`}
        path={`/auction/${a.slug || a.id}`}
        image={images[0]}
        schema={seoSchema}
      />
      <div className="mb-4"><Link to="/auctions" className="text-sm text-slate-500 hover:text-blue-600 font-semibold">← Bütün auksionlar</Link></div>
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div>
          {/* Gallery */}
          <div className="rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10]">
            <img src={images[gallery]} alt="" className="w-full h-full object-cover" data-testid="auction-main-image" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button key={`thumb-${i}-${img.slice(0, 20)}`} onClick={() => setGallery(i)} className={`flex-shrink-0 h-20 w-28 rounded-lg overflow-hidden border-2 transition-colors ${i === gallery ? "border-blue-600" : "border-transparent hover:border-slate-300"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold border ${STATUS_STYLE[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 mt-2">{a.vehicle.brand} {a.vehicle.model}</h1>
                <p className="text-slate-500 mt-1">{a.vehicle.year} • {a.vehicle.body_type} • {a.vehicle.city}</p>
              </div>
              <Button variant="outline" size="icon" onClick={toggleFav} data-testid="favorite-btn" className="rounded-full h-11 w-11"><Heart className="h-5 w-5" /></Button>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Spec icon={<Calendar className="h-4 w-4" />} label="Buraxılış" value={a.vehicle.year} />
              <Spec icon={<Gauge className="h-4 w-4" />} label="Yürüş" value={`${a.vehicle.mileage?.toLocaleString()} km`} />
              <Spec icon={<Fuel className="h-4 w-4" />} label="Yanacaq" value={a.vehicle.fuel_type} />
              <Spec icon={<Cog className="h-4 w-4" />} label="Ötürücü" value={a.vehicle.transmission} />
              <Spec icon={<Cog className="h-4 w-4" />} label="Ötürücü tipi" value={a.vehicle.drive_type} />
              <Spec icon={<Palette className="h-4 w-4" />} label="Rəng" value={a.vehicle.color} />
              <Spec icon={<Cog className="h-4 w-4" />} label="Mühərrik" value={`${a.vehicle.engine_size} L`} />
              <Spec icon={<CheckCircle2 className="h-4 w-4" />} label="Vəziyyət" value={a.vehicle.condition} />
              <Spec icon={<MapPin className="h-4 w-4" />} label="Şəhər" value={a.vehicle.city} />
            </div>

            {a.vehicle.description && (
              <div className="mt-8 p-5 rounded-2xl bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-2">Təsvir</h3>
                <p className="text-slate-700 leading-relaxed whitespace-pre-line">{a.vehicle.description}</p>
              </div>
            )}

            {/* Seller decision panel */}
            {isOwner && isEnded && a.winner_id && (
              <div className="mt-8 p-6 rounded-2xl border-2 border-amber-300 bg-amber-50">
                <h3 className="text-xl font-extrabold text-slate-900">Auksion başa çatdı — qərarınız</h3>
                <p className="mt-1 text-slate-700">Ən yüksək təklif: <span className="font-mono font-bold text-lg">{formatAZN(a.current_price)}</span></p>
                <p className="text-slate-700 mt-2 text-sm leading-relaxed">
                  Qeyd: "Bəli" seçimi alıcıya əmlakı almaq hüququ vermir və sizi satmağa məcbur etmir. Yalnız yekun təkliflə tanış olduğunuzu və öz mülahizənizə əsasən potensial alıcı ilə əlaqə saxlamaq imkanını nəzərdən keçirdiyinizi bildirir.
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Button data-testid="decide-accept" onClick={() => decide("accept")} className="rounded-xl h-14 bg-emerald-600 hover:bg-emerald-700 font-bold text-base"><CheckCircle2 className="h-5 w-5 mr-2" />BƏLİ</Button>
                  <Button data-testid="decide-reject" onClick={() => decide("reject")} variant="outline" className="rounded-xl h-14 border-2 border-red-300 text-red-700 hover:bg-red-50 font-bold text-base"><XCircle className="h-5 w-5 mr-2" />XEYR</Button>
                </div>
              </div>
            )}

            {isDecided && a.status === "accepted" && (isOwner || user?.id === a.winner_id) && (
              <div className="mt-8 p-6 rounded-2xl border-2 border-emerald-300 bg-emerald-50">
                <h3 className="text-xl font-extrabold text-slate-900">
                  {isOwner ? "Siz 'Bəli' seçmisiniz" : "Satıcı yekun qiymət təklifinizlə tanış olub"}
                </h3>
                <p className="text-slate-700 mt-2 leading-relaxed">
                  {isOwner
                    ? "Alıcı ilə əlaqə saxlamaq və ya saxlamamaq barədə qərar sizindir. 'Bəli' seçimi satış öhdəliyi yaratmır."
                    : "Satıcı sizinlə əlaqə saxlamağa qərar verərsə, əlaqə yaradacaq. Bu bildiriş əmlakın satışının təsdiqi və ya alış hüququnun yaranması demək deyil."}
                </p>
                {!isOwner && a.seller_phone && (
                  <div className="mt-4 pt-4 border-t border-emerald-200 space-y-2 text-sm">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Satıcının əlaqə məlumatları (yalnız məlumat üçün)</div>
                    <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-emerald-600" /> {a.seller_phone}</div>
                    <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-emerald-600" /> {a.seller_email}</div>
                  </div>
                )}
              </div>
            )}

            {isDecided && a.status === "rejected" && user?.id === a.winner_id && (
              <div className="mt-8 p-6 rounded-2xl border-2 border-slate-300 bg-slate-50">
                <h3 className="text-xl font-extrabold text-slate-900">Satıcı təklifinizi qəbul etmədi</h3>
                <p className="text-slate-700 mt-2 leading-relaxed">
                  Satıcı yekun qiymət təklifinizi qəbul etməyib. Auksionda iştirak etmək əmlakı almağa zəmanət vermir.
                </p>
              </div>
            )}

            {/* Bid history */}
            <div className="mt-8">
              <h3 className="font-bold text-slate-900 mb-3">Təklif tarixçəsi ({bids.length})</h3>
              {bids.length === 0 ? (
                <p className="text-slate-500 text-sm">Hələ təklif verilməyib. İlk təklifi verin!</p>
              ) : (
                <div className="rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                  {bids.map((b, i) => (
                    <div key={b.id} className={`flex items-center justify-between px-4 py-3 ${i === 0 ? "bg-emerald-50" : "bg-white"}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>{b.bidder_display?.[0] || "?"}</div>
                        <div>
                          <div className="font-semibold text-sm text-slate-900">{b.bidder_display}</div>
                          <div className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString("az-AZ")}</div>
                        </div>
                      </div>
                      <div className="font-mono font-bold text-slate-900">{formatAZN(b.amount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right sticky panel */}
        <aside className="hidden lg:block">
          <BidPanel a={a} minNext={minNext} bidAmount={bidAmount} setBidAmount={setBidAmount} placeBid={placeBid} submitting={submitting} isOwner={isOwner} />
        </aside>
      </div>

      {/* Sticky mobile bid bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-xl border-t border-slate-200 z-40">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Cari qiymət</div>
            <div className="font-mono text-lg font-bold text-slate-900">{formatAZN(a.current_price)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Qalan</div>
            <CountdownBadge endIso={a.end_time} />
          </div>
          <Button data-testid="mobile-bid-btn" disabled={a.status !== "active" || isOwner} onClick={() => document.getElementById("bid-input-mobile")?.focus()} className="rounded-full bg-blue-600 hover:bg-blue-700 font-semibold px-6 h-11">Təklif ver</Button>
        </div>
        {a.status === "active" && !isOwner && (
          <div className="flex gap-2 mt-3">
            <Input id="bid-input-mobile" data-testid="mobile-bid-input" type="number" value={bidAmount} onChange={e => setBidAmount(e.target.value)} placeholder={`min ${minNext.toFixed(0)} ₼`} className="rounded-xl h-11" />
            <Button onClick={placeBid} disabled={submitting} className="rounded-xl bg-blue-600 hover:bg-blue-700 font-semibold h-11">Ver</Button>
          </div>
        )}
      </div>
    </div>
  );
}

const Spec = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200">
    <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">{icon}</div>
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="font-semibold text-sm text-slate-900">{value}</div>
    </div>
  </div>
);

function BidPanel({ a, minNext, bidAmount, setBidAmount, placeBid, submitting, isOwner }) {
  const ms = useCountdown(a.end_time);
  const active = a.status === "active";
  return (
    <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Başlanğıc qiymət</div>
        <div className="font-mono text-base text-slate-600">{formatAZN(a.starting_price)}</div>
      </div>
      <div className="p-4 rounded-xl bg-slate-900 text-white">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Cari təklif</div>
        <div className="font-mono text-3xl font-extrabold" data-testid="current-price">{formatAZN(a.current_price)}</div>
        <div className="text-xs text-slate-400 mt-2">Təklif sayı: {a.bid_count}</div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Qalan vaxt</div>
        <div className="text-2xl mt-1"><CountdownBadge endIso={a.end_time} /></div>
      </div>
      {active && !isOwner && (
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">Minimum növbəti təklif</div>
            <div className="font-mono font-bold text-slate-900">{formatAZN(minNext)}</div>
          </div>
          <Input data-testid="bid-input" type="number" value={bidAmount} onChange={e => setBidAmount(e.target.value)} placeholder={`${minNext.toFixed(0)} AZN və ya çox`} className="rounded-xl h-12 font-mono" />
          <Button data-testid="bid-submit-button" onClick={placeBid} disabled={submitting} className="w-full rounded-xl h-14 bg-blue-600 hover:bg-blue-700 font-bold text-base shadow-lg shadow-blue-500/30">
            {submitting ? "Göndərilir..." : "Təklif ver"}
          </Button>
        </div>
      )}
      {isOwner && <div className="p-3 rounded-xl bg-blue-50 text-blue-800 text-sm">Bu sizin auksionunuzdur.</div>}
      {!active && <div className="p-3 rounded-xl bg-slate-100 text-slate-700 text-sm">Auksion {STATUS_LABELS[a.status]?.toLowerCase()}.</div>}
    </div>
  );
}
